import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { formatCurrency } from "../utils/formatters";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="10.8" cy="10.8" r="6.8" fill="none" stroke="currentColor" />
    <path d="m16 16 5 5" fill="none" stroke="currentColor" />
  </svg>
);

function SearchOverlay({ isOpen, onClose }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    inputRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      const focusableElements = Array.from(
        dialogRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      );

      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const results = useMemo(() => {
    const term = query.trim().toLocaleLowerCase(siteConfig.locale);
    if (term.length < 2) return [];

    return productData
      .filter((product) => {
        const searchableText = [
          product.name,
          product.category,
          product.collection,
          product.material,
          ...(product.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase(siteConfig.locale);
        return searchableText.includes(term);
      })
      .slice(0, 6);
  }, [query]);

  if (!isOpen) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const term = query.trim();
    if (!term) return;
    navigate(`/shop?search=${encodeURIComponent(term)}`);
    onClose();
  };

  return (
    <div className="search-overlay" role="presentation">
      <button
        className="search-overlay__backdrop"
        type="button"
        onClick={onClose}
        aria-label="Close search"
        tabIndex="-1"
      />
      <section
        className="search-overlay__dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-search-title"
      >
        <div className="search-overlay__header">
          <p className="search-overlay__eyebrow">{siteConfig.brandName}</p>
          <h2 id="site-search-title" className="search-overlay__title">
            What are you looking for?
          </h2>
          <button
            className="search-overlay__close"
            type="button"
            onClick={onClose}
            aria-label="Close search"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <form className="search-overlay__form" role="search" onSubmit={handleSubmit}>
          <label className="visually-hidden" htmlFor="store-search">
            Search the {siteConfig.brandName} catalogue
          </label>
          <input
            id="store-search"
            ref={inputRef}
            className="search-overlay__input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search knitwear, colours and materials"
            autoComplete="off"
          />
          <button className="search-overlay__submit" type="submit">
            <SearchIcon />
            <span className="visually-hidden">Search</span>
          </button>
        </form>

        <div className="search-overlay__results" aria-live="polite">
          {query.trim().length > 1 && results.length === 0 && (
            <p className="search-overlay__empty">
              No pieces found. Try another colour, style or material.
            </p>
          )}

          {results.length > 0 && (
            <>
              <p className="search-overlay__count">
                {results.length} {results.length === 1 ? "piece" : "pieces"}
              </p>
              <ul className="search-overlay__list">
                {results.map((product) => (
                  <li className="search-overlay__item" key={product.id}>
                    <Link
                      className="search-overlay__result-link"
                      to={`/shopDetails/${product.id}`}
                      onClick={onClose}
                    >
                      <img
                        className="search-overlay__image"
                        src={product.image}
                        alt=""
                      />
                      <span className="search-overlay__result-copy">
                        <span className="search-overlay__product-name">
                          {product.name}
                        </span>
                        <span className="search-overlay__product-meta">
                          {product.category} · {formatCurrency(product.price)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <button className="search-overlay__view-all" type="button" onClick={handleSubmit}>
                View all search results
              </button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default SearchOverlay;
